/**
 * FullPageLayout Component
 *
 * A full-page layout container with background and minimum height.
 * Replaces raw Tailwind like `min-h-screen bg-background`.
 *
 * @example
 * ```tsx
 * import { FullPageLayout } from '@/ui';
 *
 * <FullPageLayout>
 *   <Container>
 *     <PageHeader title="Dashboard" />
 *     <Content />
 *   </Container>
 * </FullPageLayout>
 * ```
 *
 * @see docs/ui-contract.md
 */

import * as React from 'react';
import {forwardRef} from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@/shared/utils/utils';

// =============================================================================
// VARIANTS
// =============================================================================

const fullPageLayoutVariants = cva('min-h-screen w-full', {
  variants: {
    /**
     * Background color variant
     */
    background: {
      default: 'bg-background',
      muted: 'bg-muted',
      primary: 'bg-primary',
      secondary: 'bg-secondary',
    },
  },
  defaultVariants: {
    background: 'default',
  },
});

// =============================================================================
// TYPES
// =============================================================================

interface FullPageLayoutProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof fullPageLayoutVariants> {
  /**
   * The HTML element to render as
   * @default 'div'
   */
  as?: 'div' | 'main' | 'section';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * FullPageLayout - Full-page layout container
 *
 * Use FullPageLayout as the root container for full-page layouts.
 * This replaces patterns like:
 * - `className="min-h-screen bg-background"`
 * - `className="min-h-screen bg-muted"`
 */
export const FullPageLayout = forwardRef<HTMLDivElement, FullPageLayoutProps>(
  function FullPageLayout(
    { as: Component = 'div', background, className, children, ...props },
    ref
  ) {
    return (
      <Component
        ref={ref}
        className={cn(fullPageLayoutVariants({ background }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);
